import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Form, Button, Card, Container, Alert } from 'react-bootstrap';
import { register } from '../../redux/slices/authSlice';
import styles from './Auth.module.css';
import { useTranslation } from '../../i18n';

const validationSchema = Yup.object({
  firstName: Yup.string()
    .required('שדה חובה'),
  lastName: Yup.string()
    .required('שדה חובה'),
  email: Yup.string()
    .email('כתובת דואר אלקטרוני לא תקינה')
    .required('שדה חובה'),
  password: Yup.string()
    .required('שדה חובה'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'הסיסמאות אינן תואמות')
    .required('שדה חובה')
});

const RegisterEmployee = () => {
  const { t } = useTranslation();
  const { empId, mosadSn } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error } = useSelector(state => state.auth);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(register({
        empId: empId,
        mosadSn: mosadSn,
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        password: values.password
      }));
      if (!result.error) {
        navigate('/login');
      }
    }
  });

  return (
    <Container className={styles.authContainer}>
      <Card className={styles.authCard}>
        <Card.Body>
          <h2 className={styles.authTitle}>{t('ui.auth.registerTitle')}</h2>
          
          {error && (
            <Alert variant="danger" className="mb-4">
              {error?.message}
            </Alert>
          )}

          <Form onSubmit={formik.handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>{t('ui.auth.firstName')}</Form.Label>
              <Form.Control
                type="text"
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.firstName && formik.errors.firstName}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('ui.auth.lastName')}</Form.Label>
              <Form.Control
                type="text"
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.lastName && formik.errors.lastName}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('ui.auth.email')}</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.email && formik.errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('ui.auth.password')}</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.password && formik.errors.password}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-4">
              <Form.Label>{t('ui.auth.confirmPassword')}</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.confirmPassword && formik.errors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Button
              variant="primary"
              type="submit"
              className="w-100 mb-4"
              disabled={loading}
            >
              {loading ? t('ui.auth.registerSubmitting') : t('ui.auth.registerButton')}
            </Button>

            <div className={styles.authLinks}>
              <span>{t('ui.auth.alreadyHaveAccount')}</span>
              <Link to="/login" className="ms-2">{t('ui.auth.loginButton')}</Link>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default RegisterEmployee; 